import { EnumPersonaFisicaDTORole } from '@framework/api-client/api/inquiries-compile/schemas/enumPersonaFisicaDTORole.ts';
import { type PersonaFisicaDTO } from '@framework/api-client/api/inquiries-compile/schemas/personaFisicaDTO.ts';
import { type PersonaFisicaDTOPersonaGiuridica } from '@framework/api-client/api/inquiries-compile/schemas/personaFisicaDTOPersonaGiuridica.ts';
import { type PersonaGiuridicaDTO } from '@framework/api-client/api/inquiries-compile/schemas/personaGiuridicaDTO.ts';
import { type Dictionary, groupBy, isEmpty } from 'lodash';
import { type ApplicantsTableCell } from '../../../../submissions/applicants/ApplicantsTableCell.tsx';

/**
 * Ritorna il codice fiscale del main owner di una pratica.
 * Se si tratta di un'impresa (company) ritorna la partita iva, altrimenti il codice fiscale.
 */
function getMainOwnerFiscalCode(
  applicant: PersonaFisicaDTO,
  isMainCompany: boolean,
): string {
  if (isMainCompany) {
    return (
      applicant.personaGiuridica?.partitaIva ??
      applicant.personaGiuridica?.codiceFiscale ??
      '-'
    );
  }

  return applicant.codiceFiscale ?? '-';
}

/**
 * Dato che l'id di una persona giuridica (impresa, company) non viene tornato univocamente
 * da BE, si utilizzano i seguenti campi per identificare univocamente una company:
 * - partitaIva
 * - codiceFiscale
 * - codiceAteco
 */
function getCompanyIdentifier(
  company: PersonaGiuridicaDTO | null | undefined,
): string | null {
  if (!company) return null;
  return (
    company.partitaIva ??
    company.codiceFiscale ??
    company.codiceAteco ??
    company.codiceRea ??
    '<undefined>'
  );
}

/**
 * Genera la stringa da mostrare all'utente per identeificare il main owner di una pratica.
 */
function getMainOwnerName(mainOwner: PersonaFisicaDTO, isMainCompany: boolean) {
  if (isMainCompany) {
    return mainOwner.personaGiuridica?.nome ?? '-';
  }
  if (mainOwner.cognome ?? mainOwner.nome) {
    return `${mainOwner.nome ?? ''} ${mainOwner.cognome ?? ''}`.trim();
  }

  return '';
}

function getCompanyLabel(
  companyApplicant: PersonaFisicaDTOPersonaGiuridica | undefined,
) {
  if (!companyApplicant) return 'Non identificato';

  const name = companyApplicant.nome;
  const identifier = getCompanyIdentifier(companyApplicant);

  if (name ?? identifier) {
    return `${name ? `${name} -` : ''} ${identifier ?? ''}`.trim();
  }

  return 'Non identificato';
}

/**
 * Costruisce la label per identificare un cotitolare di una pratica.
 * Se il cotitolare è una persona fisica, viene utilizzato il nome e cognome.
 * Se entrambi i campi sono vuoti, viene utilizzato il codice fiscale.
 * Se anche il codice fiscale è vuoto, viene utilizzato il messaggio "Non identificato".
 */
function getApplicantLabel(applicant: PersonaFisicaDTO | null | undefined) {
  if (!applicant) return 'Non identificato';
  const fiscalNumber = isEmpty(applicant.codiceFiscale?.trim())
    ? 'Non identificato'
    : applicant.codiceFiscale!;

  return isEmpty(applicant.nome?.trim()) || isEmpty(applicant.cognome?.trim())
    ? fiscalNumber
    : `${applicant.nome ?? ''} ${applicant.cognome ?? ''}`;
}

/**
 * Mappa i co-titolari in base alla loro tipologia (persona fisica o giuridica).
 * In caso di presenza di persona giuridica, viene utilizzato il nome dell'azienda.
 * In caso di persona fisica, viene utilizzato il nome e cognome.
 */
function mapOtherApplicants(
  applicantsByCompany: Dictionary<PersonaFisicaDTO[]>,
) {
  const result: string[] = [];
  Object.keys(applicantsByCompany).forEach(companyKey => {
    // I co-titolari associati a un'impresa hanno una key che ne identifica la company
    if (companyKey !== 'null') {
      const companyApplicant =
        applicantsByCompany[companyKey][0].personaGiuridica;
      result.push(getCompanyLabel(companyApplicant));
    }
    // Gli altri co-titolari hanno personaGiuridica = null, che il forEach trasforma in stringa 'null'
    else {
      const nonCompanyApplicants = applicantsByCompany[companyKey];

      nonCompanyApplicants.forEach(a => {
        result.push(getApplicantLabel(a));
      });
    }
  });

  return result;
}

/**
 * Mappiamo le personeFisiche in modo da poterle utilizzare nella `<ApplicantsTableCell />`
 */
export function mapApplicants(
  applicants: PersonaFisicaDTO[] | null | undefined,
): ApplicantsTableCell | null {
  if (!applicants || applicants.length === 0) return null;
  // Dopo la modifica all'API per la gestione dei soggetti, vengono tornati come titolari anche
  // i soggetti (editor/viewer). Filtriamo i soggetti per mostrare solo i titolari.
  // Rimuoviamo anche i soggetti che hanno un end date, in quanto non sono più attivi.
  const applicantList = applicants.filter(
    a => a.role === EnumPersonaFisicaDTORole.TITOLARE && !a.end,
  );
  const [main, ...otherApplicants] = applicantList;
  const isMainCompany = Boolean(main.personaGiuridica?.id);

  const byCompanies = groupBy(otherApplicants, v =>
    getCompanyIdentifier(v.personaGiuridica),
  );

  const others = mapOtherApplicants(byCompanies);

  return {
    mainOwner: getMainOwnerName(main, isMainCompany),
    mainOwnerFiscalCode: getMainOwnerFiscalCode(main, isMainCompany),
    multipleApplicants: otherApplicants.length > 0,
    others,
  };
}
